/**
 * Abstract base class for effort containers
 * Implements common logic for aggregating project efforts
 */

import {
  Month,
  MonthlyEffort,
  DateRange,
  WorkCategory,
  Status,
} from "../types";
import { Project } from "./Project";
import { IEffortContainer } from "./IEffortContainer";

/**
 * Base class that provides common implementation for containers
 * Both Category and Schedule extend this
 */
export abstract class EffortContainerBase implements IEffortContainer {
  protected dateRange: DateRange | null = null;

  /**
   * Abstract method - subclasses must implement
   * @returns Array of all projects in this container
   */
  abstract getAllProjects(): Project[];

  /**
   * Get projects filtered by status (only "In Progress" and "Planned" for effort calculations)
   * @returns Array of projects that should be included in effort calculations
   */
  public getActiveProjectsForEffortCalculation(): Project[] {
    return this.getAllProjects().filter(
      (project) =>
        project.status === Status.IN_PROGRESS ||
        project.status === Status.PLANNED
    );
  }

  /**
   * Invalidate cached date range when projects change
   */
  protected invalidateCache(): void {
    this.dateRange = null;
  }

  /**
   * Calculate the date range that encompasses all projects
   * Excludes "Done" projects from the calculation
   * Results are cached until projects change
   * @returns DateRange with start, end, and months array
   */
  getDateRange(): DateRange {
    // Return cached value if available
    if (this.dateRange) {
      return this.dateRange;
    }

    console.log(this.getAllProjects());

    // Exclude "Done" projects from date range calculation
    const projects = this.getAllProjects().filter(
      (p) => p.status !== Status.DONE
    );

    if (projects.length === 0) {
      this.dateRange = {
        startDate: new Date(),
        endDate: new Date(),
        months: [],
      };
      return this.dateRange;
    }

    const allDates: Array<Date> = projects.flatMap((p) =>
      [p.startDate, p.endDate].filter((d): d is Date => d !== undefined)
    );

    // If no projects have dates, return empty date range
    if (allDates.length === 0) {
      this.dateRange = {
        startDate: new Date(),
        endDate: new Date(),
        months: [],
      };
      return this.dateRange;
    }

    console.log("All dates", allDates);

    const minDate = new Date(
      Math.min(
        ...allDates.map((d) => {
          console.log("D is ", typeof d);
          return d.getTime();
        })
      )
    );
    const maxDate = new Date(Math.max(...allDates.map((d) => d.getTime())));

    // Round to start of first month and end of last month
    const start = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    const end = new Date(maxDate.getFullYear(), maxDate.getMonth() + 1, 0);

    // Generate array of months
    const monthsList: Month[] = [];
    let currentYear = start.getFullYear();
    let currentMonth = start.getMonth();
    const endYear = end.getFullYear();
    const endMonth = end.getMonth();

    while (
      currentYear < endYear ||
      (currentYear === endYear && currentMonth <= endMonth)
    ) {
      // Create a date for the first day of this month to get the label
      const monthDate = new Date(currentYear, currentMonth, 1);
      monthsList.push({
        year: currentYear,
        month: currentMonth,
        label: monthDate.toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        }),
      });

      // Move to next month
      currentMonth++;
      if (currentMonth > 11) {
        currentMonth = 0;
        currentYear++;
      }
    }

    this.dateRange = { startDate: start, endDate: end, months: monthsList };
    return this.dateRange;
  }

  /**
   * Get the months array from the date range
   * @returns Array of months
   */
  getMonths(): Month[] {
    return this.getDateRange().months;
  }

  /**
   * Calculate total effort for each month across all projects
   * Only includes projects with status "In Progress" or "Planned"
   * @param months - Optional array of months (defaults to container's months)
   * @returns Object mapping month index to total effort
   */
  getMonthlyTotalEffort(
    months?: Month[],
    workCategory?: WorkCategory
  ): MonthlyEffort {
    const monthsToUse = months || this.getMonths();
    const totalEffort: MonthlyEffort = {};

    // Initialize all months to 0
    monthsToUse.forEach((month, index) => {
      totalEffort[index] = 0;
    });

    // Sum effort from projects with status "In Progress" or "Planned" only
    const projects = this.getActiveProjectsForEffortCalculation();
    projects.forEach((project) => {
      const projectEffort = project.getMonthlyEffortDistribution(
        monthsToUse,
        workCategory
      );
      monthsToUse.forEach((month, index) => {
        totalEffort[index] += projectEffort[index];
      });
    });

    return totalEffort;
  }

  /**
   * Get all projects active in a specific month
   * @param month - Month to check
   * @returns Array of projects active in this month
   */
  getActiveProjectsInMonth(month: Month): Project[] {
    return this.getAllProjects().filter((project) =>
      project.isActiveInMonth(month)
    );
  }

  /**
   * Get total effort for a specific month
   * Only includes projects with status "In Progress" or "Planned"
   * @param month - Month to calculate effort for
   * @returns Total effort for the month
   */
  getTotalEffortForMonth(month: Month): number {
    return this.getActiveProjectsForEffortCalculation().reduce(
      (total, project) => {
        return total + project.getEffortForMonth(month);
      },
      0
    );
  }

  /**
   * Check if container is empty
   * @returns True if no projects in container
   */
  isEmpty(): boolean {
    return this.getAllProjects().length === 0;
  }

  /**
   * Get the number of projects in the container
   * @returns Number of projects
   */
  getProjectCount(): number {
    return this.getAllProjects().length;
  }
}
